// Pure geometry: is the pixel (clientX, clientY) inside the buffer-cell
// selection range? Extracted from TerminalHost so the math is unit-testable
// (jsdom can't measure xterm's real cell geometry). `rect`/cols/rows come from
// the rendered .xterm-screen; `sel` is xterm's getSelectionPosition().
//
// Coordinate contract (verified against @xterm/xterm 6 source, not the docs —
// the typings claim `IBufferCellPosition.x` is 1-based but the implementation
// returns the 0-based model tuples, matching our 0-based `col`):
//   • `sel.start` is INCLUSIVE — the first selected cell.
//   • `sel.end` is EXCLUSIVE — one cell PAST the last selected glyph. xterm's
//     selectionText extracts the range [start, end) via translateToString, so
//     the last selected cell is at `end.x - 1`. Hence `col < sel.end.x` on the
//     end row (NOT `<=`, which would treat the first unselected cell as inside).
//   • A multi-row selection's interior rows are carried by the col-agnostic
//     `row < sel.end.y` branch.
export interface CellRect {
  left: number;
  top: number;
  width: number;
  height: number;
}
export interface SelRange {
  start: { x: number; y: number };
  end: { x: number; y: number };
}

export function pointInSelection(
  clientX: number,
  clientY: number,
  rect: CellRect,
  cols: number,
  rows: number,
  sel: SelRange
): boolean {
  if (rect.width <= 0 || rect.height <= 0 || cols <= 0 || rows <= 0) return false;
  // Reject points outside the terminal screen entirely. Without this, a point
  // that is vertically aligned with an interior row of a multi-row selection
  // but horizontally outside the screen (padding, gutter, an adjacent pane)
  // would satisfy both row checks and falsely register as "in selection",
  // arming a drag the user never started inside the highlight.
  if (
    clientX < rect.left ||
    clientX > rect.left + rect.width ||
    clientY < rect.top ||
    clientY > rect.top + rect.height
  ) {
    return false;
  }
  const cellW = rect.width / cols;
  const cellH = rect.height / rows;
  const col = Math.floor((clientX - rect.left) / cellW);
  const row = Math.floor((clientY - rect.top) / cellH);
  const afterStart = row > sel.start.y || (row === sel.start.y && col >= sel.start.x);
  const beforeEnd = row < sel.end.y || (row === sel.end.y && col < sel.end.x);
  return afterStart && beforeEnd;
}
